// src/components/Apm/Search/SearchItem.js

import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Formik, Form, FastField } from 'formik';
import * as Yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import i18n from 'i18next';

import { Box, Stack, Typography } from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';

import { setSearchInv } from '../../../redux/slices/searchInvSlice';
import { setSearchQuote } from '../../../redux/slices/searchQuoteSlice';
import { appURL } from '../../Data/baseURL';
import Autocomplete from '../../Controls/FormsUI/AutocompleteWrapper';
import AutocompleteItemForItem from '../../Controls/FormsUI/AutocompleteItemForItem';
import ButtonBasic from '../../Controls/Buttons/ButtonBasic';
import DesktopDatePickerBasic from '../../Controls/Inputs/DesktopDatePickerBasic';
import { initialValuesSearchItem } from '../Tables/InitialValues/initialValuesSearchItem';
import SelectBasic from '../../Controls/FormsUI/SelectBasic';
import TextFieldNormal from '../../Controls/Inputs/TextFieldNormal';

const validationSchema = Yup.object({
  load_detail: Yup.string().max(50, i18n.t('yup.max_50_characters')).nullable(),
  load_address: Yup.string().max(50, i18n.t('yup.max_50_characters')).nullable(),
  unload_address: Yup.string().max(50, i18n.t('yup.max_50_characters')).nullable(),
  comment_invs: Yup.string().max(50, i18n.t('yup.max_50_characters')).nullable(),
  invoice_from: Yup.number()
    .lessThan(99999999, () => i18n.t('yup.max_8_digits'))
    .integer(() => i18n.t('yup.invoice_number_should_be_integer'))
    .typeError(() => i18n.t('yup.invoice_number_should_be_number'))
    .nullable(),
  invoice_to: Yup.number()
    .lessThan(99999999, () => i18n.t('yup.max_8_digits'))
    .integer(() => i18n.t('yup.invoice_number_should_be_integer'))
    .typeError(() => i18n.t('yup.invoice_number_should_be_number'))
    .nullable(),
});

const normalizeSearchValues = (values) => ({
  ...values,

  load_detail: values.load_detail?.trim() || '',

  load_address: values.load_address?.trim() || '',

  unload_address: values.unload_address?.trim() || '',

  comment_invs: values.comment_invs?.trim() || '',
});

const wideFieldSx = { gridColumn: { sm: 'span 2' } };

const SearchItem = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  const { searchInv } = useSelector((state) => state.searchInvSlice);

  const { searchQuote } = useSelector((state) => state.searchQuoteSlice);

  const isQuotesPage = pathname === `${appURL}/quotes`;

  const currentSearch = isQuotesPage ? searchQuote : searchInv;

  const handleFilter = (values) => {
    const normalizedValues = normalizeSearchValues(values);

    if (isQuotesPage) {
      dispatch(setSearchQuote(normalizedValues));

      return;
    }

    dispatch(setSearchInv(normalizedValues));
  };

  const handleClear = (resetForm) => {
    resetForm({
      values: initialValuesSearchItem,
    });

    if (isQuotesPage) {
      dispatch(setSearchQuote(initialValuesSearchItem));

      return;
    }

    dispatch(setSearchInv(initialValuesSearchItem));
  };

  return (
    <Formik
      enableReinitialize
      initialValues={currentSearch || initialValuesSearchItem}
      validationSchema={validationSchema}
      onSubmit={handleFilter}
    >
      {({ resetForm }) => (
        <Form autoComplete="off" style={{ width: '100%' }}>
          <Box
            sx={{
              display: 'grid',
              width: '100%',
              gridTemplateColumns: {
                xs: 'minmax(0, 1fr)',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(4, minmax(0, 1fr))',
              },
              gap: 2,
              alignItems: 'start',
              '& > *': { minWidth: 0 },
            }}
          >
            <Box sx={wideFieldSx}>
              <Autocomplete
                name="bill_to"
                label={t(isQuotesPage ? 'quote.quote_to' : 'inv.invoice_to')}
              />
            </Box>

            <Box sx={wideFieldSx}>
              <FastField as={TextFieldNormal} name="load_detail" label={t('inv.load_details')} />
            </Box>

            <Box>
              <FastField
                as={TextFieldNormal}
                name="load_address"
                label={t(isQuotesPage ? 'quote.loading_port' : 'inv.loading_address')}
              />
            </Box>

            <Box>
              <FastField
                as={TextFieldNormal}
                name="unload_address"
                label={t(isQuotesPage ? 'quote.unloading_port' : 'inv.unloading_address')}
                multiline
              />
            </Box>

            <Box>
              <DesktopDatePickerBasic name="date_min" type="date" label={t('inv.from')} />
            </Box>

            <Box>
              <DesktopDatePickerBasic name="date_max" type="date" label={t('inv.until')} />
            </Box>

            {isQuotesPage ? (
              <>
                <Box>
                  <Autocomplete name="loading_country" label={t('quote.loading_country')} />
                </Box>
                <Box>
                  <Autocomplete name="unloading_country" label={t('quote.unloading_country')} />
                </Box>
              </>
            ) : (
              <Box sx={wideFieldSx}>
                <FastField
                  as={TextFieldNormal}
                  name="comment_invs"
                  label={t('inv.note_to_invoice')}
                  multiline
                />
              </Box>
            )}

            <Box>
              <FastField
                as={TextFieldNormal}
                type="number"
                name="invoice_from"
                label={t(isQuotesPage ? 'quote.quote_hash_from' : 'inv.invoice_hash_from')}
              />
            </Box>

            <Box>
              <FastField
                as={TextFieldNormal}
                type="number"
                name="invoice_to"
                label={t(isQuotesPage ? 'quote.quote_hash_to' : 'inv.invoice_hash_to')}
              />
            </Box>

            <Box sx={wideFieldSx}>
              <AutocompleteItemForItem name="iteminv_invs" label={t('inv.invoice_item')} />
            </Box>

            <Box>
              <SelectBasic name="currency" label={t('inv.currency')} />
            </Box>

            <Box>
              <SelectBasic name="status" label={t('quote.status')} quoteorinv={isQuotesPage ? '0' : '1'} />
            </Box>

            {isQuotesPage && (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <FastField
                  as={TextFieldNormal}
                  name="comment_invs"
                  label={t('quote.note_to_quote')}
                  multiline
                />
              </Box>
            )}
          </Box>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            useFlexGap
            sx={{ mt: 2, '& .MuiButton-root': { m: 0 } }}
          >
            <ButtonBasic
              type="submit"
              color="primary"
              variant="outlined"
              sx={{ minWidth: 130 }}
              startIcon={<FilterAltIcon />}
            >
              <Typography variant="button" sx={{ px: 1 }}>
                {t('button.apply_filter')}
              </Typography>
            </ButtonBasic>

            <ButtonBasic
              type="button"
              color="secondary"
              variant="outlined"
              sx={{ minWidth: 130 }}
              startIcon={<FilterAltOffIcon />}
              onClick={() => handleClear(resetForm)}
            >
              <Typography variant="button" sx={{ px: 1 }}>
                {t('button.clear_filter')}
              </Typography>
            </ButtonBasic>
          </Stack>
        </Form>
      )}
    </Formik>
  );
};

export default SearchItem;
